/** biome-ignore-all lint/suspicious/noArrayIndexKey: no index in key */
import {useEffect, useState} from 'react';
import {isFabricWebview as isFabricWebviewCheck} from '~/common/utils/webview';
import {WTB_UPDATE_SELECTIONS} from '~/ViewInvitation/reducers/constants';
import {Button} from '~library/atoms/Button';
import {Divider} from '~library/atoms/Divider';
import {toastAlert} from '~library/atoms/Toast';
import {Typography} from '~library/atoms/Typography';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {ArrowIcon} from '~library/svg/ArrowIcon';
import {CheckmarkIcon} from '~library/svg/CheckmarkIcon';
import {DeleteIcon} from '~library/svg/DeleteIcon';
import {getUserData} from '~library/utils/auth/utils';
import {tanzanite} from '~libSass/base/_exports.sass';

const isFabricWebview = isFabricWebviewCheck();

const EditSelectionItem = ({item, removeSelection, isEditing, store}) => {
  const [quantity, setQuantity] = useState('1');

  useEffect(() => {
    if (isEditing) {
      setQuantity(item.selectedQuantity?.toString());
    }
  }, [isEditing, item]);

  const options = () => {
    const optionsArr = [];
    let optionsLength = 0;

    const existingSignup = item.selections.find((sel) => sel.user.id === window.user_id);

    if (existingSignup) {
      optionsLength = Math.max(item.remaining, 0) + existingSignup.quantity;
    } else {
      optionsLength = Math.max(item.remaining, 1);
    }

    for (let i = 0; i < optionsLength; i++) {
      optionsArr.push({
        value: (i + 1).toString(),
      });
    }

    return optionsArr;
  };

  const handleDropdownChange = (e) => {
    store.dispatch({
      type: WTB_UPDATE_SELECTIONS,
      payload: {
        type: 'update',
        item: {...item, selectedQuantity: e.target.value},
      },
    });

    setQuantity(e.target.value);
  };

  return (
    <div className="wtb-overlay__edit-selections__item">
      <Typography
        className="edit-selection__item-name"
        variant="paragraph2"
        data-qa-id="edit-selection__item-name"
      >
        {item.name || ''}
      </Typography>
      <div className="wtb-overlay__edit-selections-dropdown-container">
        <select
          className="wtb-overlay__edit-selections-dropdown"
          onChange={(e) => handleDropdownChange(e)}
          data-qa-id="edit-selection-dropdown"
          value={quantity}
        >
          {options().map((option) => (
            <option
              key={`key-${item.name.replace(/\s+/g, '')}-${option.value}`}
              value={option.value}
            >
              {option.value}
            </option>
          ))}
        </select>
      </div>
      <button
        data-qa-id="remove-wtb-selection"
        className="wtb-overlay__edit-selections__remove-btn unstyled-button"
        onClick={() => removeSelection(item.id)}
        type="button"
        aria-label="Delete"
      >
        <DeleteIcon ratio={1.05} color={tanzanite} />
      </button>
    </div>
  );
};

export const EditSelection = ({
  backToList,
  pendingSelections,
  isEditing,
  onAssign,
  onUnassign,
  store,
  ref,
}) => {
  const userData = getUserData(window.isWebview ?? isFabricWebview);

  const [processing, setProcessing] = useState(false);

  const removeSelection = async (id) => {
    setProcessing(true);

    await onUnassign(window.user_id, userData.guestName, id)
      .then(() => {
        store.dispatch({
          type: WTB_UPDATE_SELECTIONS,
          payload: {
            type: 'remove',
            item: {id},
          },
        });

        setProcessing(false);
        if (pendingSelections.length === 1) {
          backToList();
          toastAlert('Your changes were saved.', {
            type: 'success',
            icon: <CheckmarkIcon ratio={0.5} />,
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            id: 'wtb-overlay__toast__text',
          });
        }
      })
      .catch((error) => {
        evite.error('Error during onUnassign:', error);
        setProcessing(false);
      });
  };

  const handleSaveSelections = async () => {
    try {
      const assignPromises = pendingSelections.map((item) =>
        onAssign(userData.userId, userData.guestName, item.id, item.selectedQuantity)
      );

      await Promise.all(assignPromises);

      toastAlert('Your changes were saved.', {
        type: 'success',
        icon: <CheckmarkIcon ratio={0.5} />,
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        id: 'wtb-overlay__toast__text',
      });

      backToList();
    } catch (error) {
      toastAlert(error, {
        type: 'error',
        icon: <AlertLineIcon ratio={0.65} />,
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        id: 'wtb-overlay__toast__text',
      });
    }
  };

  return (
    <div className="wtb-overlay__edit-selections-page" ref={ref}>
      <div className="wtb-overlay__body">
        <div className="wtb-overlay__heading">
          <button
            data-qa-id="wtb-overlay-back-btn"
            className="wtb-overlay__back-btn unstyled-button"
            onClick={() => backToList()}
            type="button"
            aria-label="Back"
          >
            <ArrowIcon direction="left" />
          </button>
          <Typography
            className="wtb-overlay__title"
            variant="paragraph2"
            data-qa-id="what-to-bring-overlay-header"
          >
            Edit Selection
          </Typography>
        </div>
        <Divider marginBottom="1.5rem" />
        {pendingSelections.map((item, key) => (
          <EditSelectionItem
            item={item}
            store={store}
            key={`selection-item-${key}`}
            removeSelection={removeSelection}
            isEditing={isEditing}
          />
        ))}
      </div>
      <div className="wtb-overlay__footer">
        <Button
          variant="primary"
          size="large"
          className="wtb-overlay__save-selections"
          onClick={() => handleSaveSelections()}
          fullWidth
          data-qa-id="wtb-overlay-save-selections-btn"
        >
          {processing && <span className="evt-spinner inline" />} Save
        </Button>
      </div>
    </div>
  );
};
